import { RawDraftContentState } from 'draft-js';
import { isUndefined } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';

import { Button } from '../button/Button';
import { SpaceBetweenButtonWrapper } from '../button/SpaceBetweenButtonWrapper';
import { Table } from '../icons';
import { LongText } from '../longtext/LongText';
import { ModalHeader } from '../modal/ModalHeader';
import { Text } from '../text/Text';
import styles from './Report.module.scss';
import { Toggle } from '../toggle';
import { ReportEmailContent } from './ReportEmailContent';
import { Scrollable } from '../scrollable/Scrollable';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { fetchAllBasicUsersStarted } from '../../admin/users/store';
import { SectionWrapper } from './SectionWrapper';

interface GenerateReportModalProps {
    isOpen: boolean;
    reportHasUpdated: boolean;
    closeModal: () => void;
    generateReport: (shouldDownload: boolean, shouldEmail: boolean, shouldSave: boolean, shouldSaveAs: boolean) => void;
    templateId: number | undefined;
    selectedUserIds: number[];
    templateName: string;
    templateDescription: string;
    emailBody: RawDraftContentState | null;
    updateReportValue: (key: 'templateName' | 'templateDescription' | 'templateUserIds' | 'templateEmailBody', value: string | number[] | RawDraftContentState | null) => void;
    testId: string;
}

export const GenerateReportModal: React.FC<GenerateReportModalProps> = ({
    isOpen,
    reportHasUpdated,
    closeModal,
    generateReport,
    templateId,
    templateName,
    selectedUserIds,
    emailBody,
    updateReportValue,
    templateDescription,
    testId
}) => {
    useFetchStarted([fetchAllBasicUsersStarted()]);

    const [shouldDownload, setShouldDownload] = useState<boolean>(true);
    const [shouldEmail, setShouldEmail] = useState<boolean>(false);
    const [shouldSave, setShouldSave] = useState<boolean>(false);
    const [shouldSaveAs, setShouldSaveAs] = useState<boolean>(false);

    const generate = useCallback(() => generateReport(shouldDownload, shouldEmail, shouldSave, shouldSaveAs && !isUndefined(templateId)), [generateReport, shouldDownload, shouldEmail, shouldSave, shouldSaveAs, templateId]);
    const generateLabel = useMemo(() => {
        if (shouldSave || shouldSaveAs) {
            return `${shouldEmail && !shouldDownload ? 'Email' : 'Generate'} & Save`;
        }
        return shouldEmail && !shouldDownload ? 'Email' : 'Generate';
    }, [shouldSave, shouldSaveAs, shouldEmail, shouldDownload]);
    const generateDisabled = useMemo(() => (shouldEmail && selectedUserIds.length === 0) || ((shouldSave || shouldSaveAs) && templateName === '') || (!shouldDownload && !shouldEmail), [shouldSave, shouldEmail, shouldSaveAs, templateName, selectedUserIds, shouldDownload]);

    const generateDisabledTooltip = useMemo(() => {
        let disabledTooltip = [];
        if (shouldEmail && selectedUserIds.length === 0) {
            disabledTooltip.push('You must select at least one user if you would like to send this report as an email');
        }
        if ((shouldSave || shouldSaveAs) && templateName === '') {
            disabledTooltip.push('You must provide a name if you wish to save this report as a template');
        }
        return disabledTooltip;
    }, [shouldEmail, shouldSave, shouldSaveAs, selectedUserIds, templateName]);

    const getToggleOption = useCallback((label: string, toggle: (value: boolean) => void, isSelected: boolean) => (
        <div className={styles.toggleOptionWrapper}>
            <div className={styles.toggleOptionLabel}>{label}</div>
            <div className={styles.toggleOption}>
                <Toggle checked={isSelected} onChange={toggle} />
            </div>
        </div>
    ), []);

    useEffect(() => {
        if (shouldSave && shouldSaveAs) {
            setShouldSave(false);
        }
    }, [shouldSave, shouldSaveAs]);

    const updateName = useCallback((value: string) => updateReportValue('templateName', value), [updateReportValue]);
    const updateDescription = useCallback((value: string) => updateReportValue('templateDescription', value), [updateReportValue]);
    const updateSelectedUserIds = useCallback((value: number[]) => updateReportValue('templateUserIds', value), [updateReportValue]);
    const updateEmailBody = useCallback((value: RawDraftContentState | null) => updateReportValue('templateEmailBody', value), [updateReportValue]);

    const closeAndReset = useCallback(() => {
        closeModal();
        setShouldDownload(true);
        setShouldEmail(false);
        setShouldSave(false);
        setShouldSaveAs(false);
    }, [closeModal]);

    return (
        <Modal
            isOpen={isOpen}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
            style={{ overlay: { display: 'flex' } }}
            className={styles.generateReportModal}
            ariaHideApp={false}
        >
            <div className={styles.generateModalWrapper}>
                <ModalHeader label='Generate Report' icon={Table} />
                <div className={styles.generateReportWrapper}>
                    <Scrollable>
                        <div className={styles.generateReportLabel}>Please select which of the following you would like to do when generating your report</div>
                        <div className={styles.toggleOptionsWrapper}>
                            {getToggleOption('Download', setShouldDownload, shouldDownload)}
                            {getToggleOption('Email', setShouldEmail, shouldEmail)}
                            {reportHasUpdated && getToggleOption('Save Template', setShouldSave, shouldSave)}
                            {reportHasUpdated && !isUndefined(templateId) && getToggleOption('Save New Template', setShouldSaveAs, shouldSaveAs)}
                        </div>
                        {shouldEmail &&
                            <SectionWrapper id={`${testId}-email-content`} label='Email Details' description='Select the users you would like to email this report to and add an optional personal message'>
                                <ReportEmailContent emailBody={emailBody} selectedUserIds={selectedUserIds} testId={testId} updateEmailBody={updateEmailBody} updateSelectedUserIds={updateSelectedUserIds} />
                            </SectionWrapper>
                        }
                        {reportHasUpdated &&
                            <div className={styles.saveTemplateWrapper}>
                                <SectionWrapper id={`${testId}-template-details`} label='Template Details'>
                                    <div className={styles.templateDetailsWrapper}>
                                        <Text
                                            testId={`save-report-template-${testId}-name`}
                                            label='Template Name'
                                            value={templateName}
                                            onChange={e => updateName(e.target.value)}
                                            maxLength={100}
                                            disabled={!shouldSave && !shouldSaveAs}
                                        />
                                        <LongText
                                            testId={`save-report-template-${testId}-description`}
                                            label='Description'
                                            width='100%'
                                            value={templateDescription}
                                            onChange={updateDescription}
                                            disabled={!shouldSave && !shouldSaveAs}
                                        />
                                    </div>
                                </SectionWrapper>
                            </div>
                        }
                    </Scrollable>
                </div>
                <SpaceBetweenButtonWrapper leftButtons={[<Button label='Close' onClick={closeAndReset} key='close' />]} rightButtons={[<Button label={generateLabel} onClick={generate} disabled={generateDisabled} key='generate' disabledTooltip={generateDisabledTooltip} />]} />
            </div>
        </Modal>
    );
};
